<template>
	<section id="top" class="hero">
		<div class="hero__pattern" aria-hidden="true" />

		<div class="container hero__inner">
			<p v-reveal class="hero__kicker">{{ t('hero.kicker') }}</p>

			<h1 v-reveal="60" class="hero__brand">BEETRONIX</h1>

			<p v-reveal="120" class="hero__title">{{ t('hero.title') }}</p>
			<p v-reveal="180" class="hero__tagline">{{ t('hero.tagline') }}</p>
			<p v-reveal="240" class="hero__lead">{{ t('hero.lead') }}</p>

			<div v-reveal="300" class="hero__cta">
				<a
					:href="ownProfile.href"
					:download="ownProfile.name"
					class="btn btn--gold"
				>
					<app-icon name="download" />
					<span>{{ t('hero.cta-own') }}</span>
				</a>

				<a
					:href="otherProfile.href"
					:download="otherProfile.name"
					class="btn btn--ghost"
				>
					<app-icon name="download" />
					<span>{{ t('hero.cta-other') }}</span>
				</a>
			</div>
		</div>

		<img
			:src="assetUrl('hero-bee.webp')"
			class="hero__bee"
			alt=""
			width="1500"
			height="1161"
			fetchpriority="high"
		>
	</section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import { PROFILE_FILES } from '@/helpers/constants'
import { assetUrl } from '@/helpers/functions'

defineOptions({ name: 'HeroSection' })

const { t, locale } = useI18n()

const ownProfile = computed(() => PROFILE_FILES[locale.value] || PROFILE_FILES.en)
const otherProfile = computed(() => PROFILE_FILES[locale.value === 'ar' ? 'en' : 'ar'])
</script>

<style scoped lang="scss">
.hero {
	position: relative;
	overflow: hidden;
	isolation: isolate;
	padding-top: calc(var(--header-h) + clamp(46px, 7vh, 90px));
	text-align: center;
	background: radial-gradient(120% 90% at 50% 0%, #fbfbfb 0%, #f1f1f2 46%, #e6e6e8 100%);
}

// the chevron watermark that runs through the printed profile
.hero__pattern {
	position: absolute;
	inset: 0;
	z-index: -1;
	opacity: 0.55;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='80' viewBox='0 0 160 80'%3E%3Cg fill='none' stroke='%230d0d0d' stroke-opacity='0.055' stroke-width='11'%3E%3Cpath d='M-10 4 L30 44 L70 4 L110 44 L150 4 L190 44'/%3E%3Cpath d='M-10 44 L30 84 L70 44 L110 84 L150 44 L190 84'/%3E%3C/g%3E%3C/svg%3E");
	background-size: clamp(120px, 14vw, 190px) auto;
	mask-image: linear-gradient(180deg, #000 0%, #000 34%, transparent 74%);
	-webkit-mask-image: linear-gradient(180deg, #000 0%, #000 34%, transparent 74%);
}

.hero__inner {
	position: relative;
	z-index: 2;
	max-width: 940px;
}

.hero__kicker {
	font-size: 0.76rem;
	font-weight: 600;
	letter-spacing: 0.42em;
	text-transform: uppercase;
	color: #7a7a7a;

	@include arabic {
		letter-spacing: 0.12em;
	}
}

.hero__brand {
	margin-top: 18px;
	font-size: clamp(2.9rem, 11.5vw, 7.4rem);
	font-weight: 800;
	letter-spacing: -0.035em;
	line-height: 0.95;
	color: $color-ink;

	@include arabic {
		letter-spacing: -0.02em;
	}
}

.hero__title {
	margin-top: 6px;
	font-size: clamp(1.4rem, 4.4vw, 2.5rem);
	font-weight: 700;
	font-style: italic;
	color: $color-ink;

	@include arabic {
		font-style: normal;
		font-weight: 800;
	}
}

.hero__tagline {
	margin-top: 10px;
	font-size: clamp(1.02rem, 2.5vw, 1.5rem);
	font-weight: 300;
	letter-spacing: 0.14em;
	color: #4f4f4f;

	@include arabic {
		letter-spacing: 0.02em;
		font-weight: 400;
	}
}

.hero__lead {
	margin: 26px auto 0;
	max-width: 56ch;
	color: #555;
}

.hero__cta {
	margin-top: clamp(28px, 4vh, 44px);
	display: flex;
	gap: 14px;
	justify-content: center;
	flex-wrap: wrap;

	@include below($bp-mobile) {
		width: 100%;
	}
}

.hero__bee {
	position: relative;
	z-index: 1;
	width: min(1120px, 132%);
	height: auto;
	margin: clamp(-8px, -1vh, 0px) auto calc(-1 * clamp(28px, 7vw, 88px));
	pointer-events: none;
	user-select: none;
}
</style>
